"use client";

import { useCallback, useState } from "react";
import { useTranslations } from "use-intl";

import { MusicIcon, ReleaseFormatIcon, ReleaseIcon, StyleIcon } from "@/components/appIcons";
import type { LinkTarget } from "@/components/link";
import { useFormatNumber } from "@/hooks/use-format-number";
import { formatCatalogTaxonomyLabel } from "@/lib/music-taxonomy";
import { ALBUM_TYPES } from "@garimpa/lib/albumTypes";
import type { Filter } from "@garimpa/lib/facetFilter";
import { FORMAT_CATEGORIES } from "@garimpa/lib/formatGroups";
import type { ReleaseSearchFilterKey } from "@garimpa/lib/searchState";

import { FacetOptionRows } from "./facet-option-list";
import { FacetSelect } from "./facet-select";
import {
  ALBUM_TYPE_ICONS,
  FORMAT_CATEGORY_ICONS,
  FORMAT_LABEL_KEYS,
} from "./search-taxonomy-options";
import { useSearchFacetOptions } from "./use-search-facet-options";

const INPUT_CLASS =
  "w-full px-3 py-2 text-sm bg-secondary border border-border rounded-md focus-within:outline-none focus-within:ring-1 focus-within:ring-primary cursor-pointer";

interface FacetCount {
  value: string;
  count: number;
}

export function SearchTaxonomyFilters({
  genre,
  style,
  type,
  format,
  genreCounts,
  styleCounts,
  typeCounts,
  formatCounts,
  filter,
  updateFilter,
  updateFilters,
  getFacetLinkTarget,
}: {
  genre: string | undefined;
  style: string | undefined;
  type: (typeof ALBUM_TYPES)[number] | undefined;
  format: (typeof FORMAT_CATEGORIES)[number] | undefined;
  genreCounts: FacetCount[];
  styleCounts: FacetCount[];
  typeCounts: Record<string, number>;
  formatCounts: Record<string, number>;
  filter: Filter | undefined;
  updateFilter: (key: string, value: string | number | boolean | undefined) => void;
  updateFilters: (updates: Record<string, string | number | undefined>) => void;
  getFacetLinkTarget?: (key: ReleaseSearchFilterKey, value: string) => LinkTarget;
}) {
  const t = useTranslations("globals.search");
  const tt = useTranslations("globals.search.albumType");
  const tg = useTranslations("shared.genres");
  const ts = useTranslations("shared.styles");
  const formatNumber = useFormatNumber();
  const [genreSearch, setGenreSearch] = useState("");
  const [styleSearch, setStyleSearch] = useState("");
  const renderGenreLabel = useCallback(
    (value: string) => {
      const key = value as Parameters<typeof tg>[0];
      return tg.has(key) ? tg(key) : formatCatalogTaxonomyLabel(value);
    },
    [tg],
  );
  const renderStyleLabel = useCallback(
    (value: string) => {
      const key = value as Parameters<typeof ts>[0];
      return ts.has(key) ? ts(key) : formatCatalogTaxonomyLabel(value);
    },
    [ts],
  );
  const matchesGenreSearch = useCallback(
    (item: FacetCount, query: string) =>
      item.value.toLowerCase().includes(query) ||
      renderGenreLabel(item.value).toLowerCase().includes(query),
    [renderGenreLabel],
  );
  const matchesStyleSearch = useCallback(
    (item: FacetCount, query: string) =>
      item.value.toLowerCase().includes(query) ||
      renderStyleLabel(item.value).toLowerCase().includes(query),
    [renderStyleLabel],
  );
  const genreOptions = useSearchFacetOptions({
    facet: "genre",
    filter,
    initialItems: genreCounts,
    query: genreSearch,
    selectedValues: genre ? [genre] : [],
  });
  const styleOptions = useSearchFacetOptions({
    facet: "style",
    filter,
    initialItems: styleCounts,
    query: styleSearch,
    selectedValues: style ? [style] : [],
  });
  return (
    <>
      <div>
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
          <MusicIcon className="text-sm" /> {t("genre")}
        </h3>
        <FacetSelect
          items={genreOptions.items}
          value={genre}
          onChange={(value) => updateFilters({ genre: value, style: undefined })}
          placeholder={t("genrePlaceholder")}
          className={INPUT_CLASS}
          renderLabel={renderGenreLabel}
          matchesSearch={matchesGenreSearch}
          filterSearchItems={!genreOptions.remoteSearchActive}
          onSearchChange={setGenreSearch}
        />
      </div>
      <div>
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
          <StyleIcon className="text-sm" /> {t("style")}
        </h3>
        <FacetSelect
          items={styleOptions.items}
          value={style}
          onChange={(value) => updateFilter("style", value)}
          placeholder={t("stylePlaceholder")}
          className={INPUT_CLASS}
          renderLabel={renderStyleLabel}
          matchesSearch={matchesStyleSearch}
          filterSearchItems={!styleOptions.remoteSearchActive}
          onSearchChange={setStyleSearch}
        />
      </div>
      <hr className="border-border" />
      <div>
        <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
          <ReleaseIcon className="text-sm" /> {t("type")}
        </h3>
        <FacetOptionRows
          items={ALBUM_TYPES.map((albumType) => {
            const count = typeCounts[albumType] ?? null;
            return {
              value: albumType,
              label: tt(albumType),
              icon: ALBUM_TYPE_ICONS[albumType],
              count,
              disabled: count === 0 && type !== albumType,
              link: getFacetLinkTarget?.("type", albumType),
            };
          })}
          value={type}
          onChange={(value) => updateFilter("type", value)}
          formatCount={formatNumber}
        />
      </div>
      <hr className="border-border" />
      <div>
        <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
          <ReleaseFormatIcon className="text-sm" /> {t("format")}
        </h3>
        <FacetOptionRows
          items={FORMAT_CATEGORIES.map((value) => {
            const count = formatCounts[value] ?? null;
            return {
              value,
              label: t(FORMAT_LABEL_KEYS[value]),
              icon: FORMAT_CATEGORY_ICONS[value],
              count,
              disabled: count === 0 && format !== value,
              link: getFacetLinkTarget?.("format", value),
            };
          })}
          value={format}
          onChange={(value) => updateFilter("format", value)}
          formatCount={formatNumber}
        />
      </div>
      <hr className="border-border" />
    </>
  );
}
